/**
 * widgets/StepperBanner.ts · v353 业务流程 stepper 指示 (复用 v200 v170 TN_FLOW_STEPPER)
 *
 * 7 页 inject 4-step indicator (报价 → 销售订单 → 工单 → Gantt 排程)
 * 当前页高亮 · 已完成 ✓ · 未到灰 · click 跳页
 *
 * 用法:
 *   import { renderStepperBanner } from '@widgets/StepperBanner';
 *   c.appendChild(renderStepperBanner('quotationCreate'));
 *
 * 5 流程定义:
 *   - sales: 报价 → SO → 工单 → Gantt
 *   - purchasing: PR → 询价 → PO → 收货
 *   - finance: SO → 开 AR → 收款 → 账龄
 *   - quality: IQC → IPQC → OQC → 客诉
 *   - production: 工单 → 派工 → 报工 → 完工
 */

export interface FlowStep {
  page: string;
  icon: string;
  zh: string;
  vi: string;
}

const FLOWS: Record<string, FlowStep[]> = {
  sales: [
    { page: 'quotationCreate', icon: '📝', zh: '报价', vi: 'Báo giá' },
    { page: 'salesOrderList',  icon: '📋', zh: '销售订单', vi: 'Đơn hàng' },
    { page: 'woManage',         icon: '🏭', zh: '工单', vi: 'LSX' },
    { page: 'ganttSchedule',   icon: '📅', zh: 'Gantt 排程', vi: 'Lịch SX' },
  ],
  purchasing: [
    { page: 'prCreate', icon: '📝', zh: '采购请求', vi: 'Đề xuất' },
    { page: 'rfqList',  icon: '💬', zh: '询价', vi: 'RFQ' },
    { page: 'poCreate', icon: '📋', zh: '采购订单', vi: 'PO' },
    { page: 'receiveMaterial', icon: '📥', zh: '收货', vi: 'Nhận hàng' },
  ],
  finance: [
    { page: 'salesOrderList',  icon: '📋', zh: '销售订单', vi: 'Đơn hàng' },
    { page: 'arInvoiceCreate', icon: '🧾', zh: '开 AR', vi: 'Xuất HĐ' },
    { page: 'customerPaymentCreate', icon: '💵', zh: '录收款', vi: 'Thu tiền' },
    { page: 'arAging',          icon: '📊', zh: '账龄追踪', vi: 'Tuổi nợ' },
  ],
  quality: [
    { page: 'iqcIncoming', icon: '📥', zh: 'IQC 进货', vi: 'IQC' },
    { page: 'ipqcInline',  icon: '⚙️', zh: 'IPQC 在制', vi: 'IPQC' },
    { page: 'oqcOutgoing', icon: '📤', zh: 'OQC 出厂', vi: 'OQC' },
    { page: 'complaintsCenter', icon: '🚨', zh: '客诉', vi: 'Khiếu nại' },
  ],
  production: [
    { page: 'woCreate',  icon: '🆕', zh: '新建工单', vi: 'Tạo LSX' },
    { page: 'scanStart', icon: '📷', zh: '扫码开工', vi: 'Quét bắt đầu' },
    { page: 'scanReport', icon: '📊', zh: '报工', vi: 'Báo công' },
    { page: 'woTracking', icon: '✅', zh: '完工跟踪', vi: 'Hoàn thành' },
  ],
};

/**
 * 自动从当前 page 找到所属流程 · 找不到返 null (不显)
 */
function findFlow(currentPage: string): { flowId: string; activeIdx: number } | null {
  for (const [flowId, steps] of Object.entries(FLOWS)) {
    const idx = steps.findIndex(s => s.page === currentPage);
    if (idx >= 0) return { flowId, activeIdx: idx };
  }
  return null;
}

/**
 * 渲染 stepper banner · current page 高亮
 */
export function renderStepperBanner(currentPage: string): HTMLElement | null {
  const found = findFlow(currentPage);
  if (!found) return null;
  const steps = FLOWS[found.flowId];
  const { activeIdx } = found;

  const banner = document.createElement('div');
  banner.className = 'tn-stepper-banner';
  banner.style.cssText = `
    background:linear-gradient(135deg,#eff6ff,#dbeafe);
    border:1px solid #93c5fd;
    border-radius:8px;
    padding:10px 14px;
    margin-bottom:14px;
    display:flex; align-items:center; gap:8px;
    overflow-x:auto;
  `;

  steps.forEach((step, i) => {
    const isCompleted = i < activeIdx;
    const isActive = i === activeIdx;
    // step pill
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.style.cssText = `
      display:inline-flex; align-items:center; gap:4px;
      padding:4px 10px; border-radius:14px; border:0;
      font-size:12px; font-weight:600; cursor:pointer;
      transition:transform 0.1s, background 0.15s;
      white-space:nowrap;
      ${isActive ? 'background:#1e40af; color:#fff; box-shadow:0 1px 4px rgba(30,64,175,0.4);'
        : isCompleted ? 'background:#10b981; color:#fff;'
        : 'background:#fff; color:#6b7280; border:1px solid #e5e7eb;'}
    `;
    pill.title = `${step.zh} · ${step.vi}`;
    pill.innerHTML = `
      <span>${isCompleted ? '✓' : step.icon}</span>
      <span>${escapeHtml(step.zh)}</span>
    `;
    pill.onclick = () => { window.location.hash = `#${step.page}`; };
    banner.appendChild(pill);

    // separator arrow (除最后一个)
    if (i < steps.length - 1) {
      const arrow = document.createElement('span');
      arrow.style.cssText = `color:${i < activeIdx ? '#10b981' : '#9ca3af'}; font-size:12px; font-weight:bold;`;
      arrow.textContent = '→';
      banner.appendChild(arrow);
    }
  });

  return banner;
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
